<template>
  <div class="container p-0">

    <h5>
      {{ $t('Campaign.overview.title') }}
    </h5>

    <campaign-grid
      :activeDate="getViewedDate"
      :listItems="getDisplayItems"
      :removing="removingEvents"
      :defaultStyle="getDisplayStyle"
      :today="getToday"
      :dateRangePickerPresets="getDateRangePresets"
      allowRangePicker
      useCalendarNavigation
      campaignPreviewItems
      dataSource="campaign"
      ref="campaignGrid"
      @setSchedule="setSchedule"
      @changeDate="setFilteredDate"
      @setDisplayStyle="setDisplayStyle"
      @editMpe="editCampaign"
      @removeMpe="removeCampaign"
      @duplicate="duplicate" />

    <editor-wrapper
      v-if="isEditingMpe"
      @discardChanges="discardChanges"
      @saveMpe="saveMpe"
      :mpeId="getEditedMpeId" />

  </div>
</template>

<script>
import CampaignGrid from '@/components/standard/display/CampaignGrid'

import moment from 'moment'
import MomentRange from 'moment-ranger'
import apiConnector from '@/mixins/apiConnector.js'
import campaignViewer from '@/mixins/pagination/providers/campaignViewer'
import EditorWrapper from '@/components/service/campaignManager/standaloneMpe/EditorWrapper'
import fieldBuilder from '@/assets/modules/apollo/fieldBuilder'

import nManager from '@/assets/modules/notifications/manager'
import resolver from '@/assets/modules/error/resolver'

import routingHelper from '@/assets/modules/routing/routingHelper'
import makeDates from '@/assets/modules/displayGrids/makeDates'
import drpg from '@/assets/modules/inputs/dateRangePresetGenerator'

const DEFAULT_EDITOR_VIEW = 'grid'
const TYPENAME_DELETE_MUTATIONS = { Campaign: 'deleteCampaign', Advertisable: 'deleteAdvertisable', MediaPlanEvent: 'deleteMediaPlanEvent' }

export default {
  data () {
    return {
      filterFields: {
        channel: null,
        type: null,
        date: null
      },

      pullNewMpes: true,

      // Default previus ranges for grid and calendar
      previousRanges: {},

      removingEvents: {}
    }
  },

  methods: {
    /**
     * Refetches campaigns
     */
    refetchCampaigns () {
      this.$apollo.queries.campaigns.refresh()
      this.$apollo.queries.firstLast.refresh()
      this.$apollo.queries.mediaPlanEvents.refresh()
    },

    /**
     * Sets the new schedule for the given event/campaign
     */
    setSchedule (e) {
      let { expiresAt, id, publishAt, type } = e
      let isCampaign = resolver.isCampaign(type)
      let mutationName = (isCampaign) ? 'updateCampaign' : 'storeMpe'

      let okCallback = (e) => {
        nManager.okNotification(this, `${isCampaign ? 'Notification.ok.campaignUpdated.label' : 'Notification.ok.eventUpdated.label'}`)
        
        this.refetchCampaigns()
      }

      let failCallback = (_, e) => {
        let message = `${isCampaign ? 'Kampanja ni bila posodobljena' : 'Dogodek ni bil posodobljen'}`
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)
      }

      let gqlObject = {
        startsAt: publishAt,
        expiresAt,
        id
      }

      if (isCampaign) {
        gqlObject.type = type
      }

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(gqlObject, mutationName, 'mutation', [], {
          type: 'CampaignType'
        })

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * Sets the new grid display style
     * @param {String} style New grid display style
     */
    setDisplayStyle (style) {
      // If clicked on same display style just stop
      if (this.getDisplayStyle === style) return
      this.$router.push({ name: 'viewCampaigns', params: { style } })

      if (resolver.isCalendarView(style) || resolver.isCalendarView(this.getDisplayStyle)) {
        this.refetchCampaigns()
      }
    },

    /**
     * Sets new filter dates
     * @param {Object} e Date range object
     */
    setFilteredDate (e) {
      let { from, to, id } = e

      let date = {
        from,
        to,
        id
      }

      let view = resolver.isCalendarView(this.getDisplayStyle) ? 'calendar' : 'grid'
      this.$set(this.previousRanges, view, date)
      this.$set(this.filterFields, 'date', date)

      // Refresh data if it was a calendar view
      this.refetchCampaigns()
    },

    /**
     * Set's event's status as resolving
     * @param {String} id
     */
    markEventAsRemoving (id) {
      this.$set(this.removingEvents, id, true)
    },

    /**
     * Set's event's status as resolved
     * @param {String} id
     */
    markEventAsRemoved (id) {
      this.$delete(this.removingEvents, id)
    },

    /**
     * Removes the campaign
     * @param {Object} e Campaign to be removed
     */
    removeCampaign (e) {
      let { id, type, fromType } = e
      this.markEventAsRemoving(id)

      let mutationName = TYPENAME_DELETE_MUTATIONS[fromType]

      let okCallback = (e) => {
        this.markEventAsRemoved(id)
        this.refetchCampaigns()
      }

      let failCallback = (_, e) => {
        this.markEventAsRemoved(id)
        let message = `${isCampaign ? 'Kampanja' : 'Dogodek'} ni bil odstranjen!`
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)
      }

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(e, mutationName, 'mutation')

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * Goes to event/campaign editor based on event type
     * @param {Object} e Event to be edited
     */
    editCampaign (e) {
      let { id, type, fromType } = e
      if (fromType === 'Campaign') {
        this.$router.push({ name: 'editCampaign', params: { id, view: DEFAULT_EDITOR_VIEW } })
      } else if (fromType === 'Advertisable') {
        this.$router.push({ name: 'editQuickPostGroup', params: { id, view: DEFAULT_EDITOR_VIEW } })
      } else {
        this.$router.push({ name: 'viewCampaigns', params: { mpeId: id, style: this.getDisplayStyle } })
      }
    },

    /**
     * Discards currently edited MPE
     */
    discardChanges (e) {
      this.$router.push({ name: 'viewCampaigns', params: { style: this.getDisplayStyle  } })
    },

    /**
     * Saves curently edited MPE
     */
    saveMpe (e) {
      let gqlObject = {
        id: this.getEditedMpeId,
        advertisable: e.params.advertisable,
        publishAt: e.params.startsAt,
        removeAt: e.params.expiresAt,
        type: e.type
      }
      gqlObject[e.type] = e.payload

      let media = (Object.keys(e.payloadMedia || {})).map(m => { return { url: m } })
      gqlObject[e.type][resolver.getMediaFieldName(e.type)] = media

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(gqlObject, 'storeMpe', 'mutation', [], {
          type: 'MediaPlanEventType',
          status: 'CampaignStatus'
        })

      let okCallback = (e) => {
        nManager.okNotification(this, 'Notification.ok.updatesSaved.label')
        this.$set(this, 'mediaPlanEvents', {})
        this.refetchCampaigns()
        this.discardChanges({})
      }

      let failCallback = (_, e) => {
        let message = 'Spremembe se niso shranile'
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)

        this.discardChanges({})
      }

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * Duplicates given object. If it is a MPE, store it without id, than open MPE editor.
     * If it is a campaign go to campaign creator.
     */
    duplicate (e) {
      let { id, fromType, eventType, advertisable, advertisableType } = e

      if (fromType === 'Campaign') {
        // It's a campaign...
        this.duplicateCampaign(id, eventType)
      } else if (fromType === 'Advertisable') {
        this.duplicateAdvertisable(id, eventType)
      } else {
        this.duplicateMpe(id, advertisableType)
      }
    }
  },

  computed: {
    isFiltering () {
      return Object.values(this.filterFields).reduce((acc, ff) => acc || !!ff, false)
    },

    /**
     * Gives daterange picker presets
     * @returns {Array}
     */
    getDateRangePresets () {
      let { first, last } = this.getCampaignRange
      let start = moment(first || moment()).add(1, 'day').startOf('day').toDate()
      let end = moment(last || moment()).add(1, 'day').startOf('day').toDate()
      let label = this.$t('DateRange.preset.allCampaigns')

      let rtr = {
        allCampaigns: function () {
          return {
            label: label || 'Vse kampanje',
            active: false,
            dateRange: {
              start: start,
              end: end
            }
          }
        }
      }
      return Object.assign({}, rtr, drpg.generate(
        ['today', 'thisMonth', 'lastMonth', 'last7days', 'last30days'],
        {
          'today': this.$t('DateRange.preset.today'),
          'thisMonth': this.$t('DateRange.preset.thisMonth'),
          'lastMonth': this.$t('DateRange.preset.lastMonth'),
          'last7days': this.$t('DateRange.preset.last7days'),
          'last30days': this.$t('DateRange.preset.last30days')
        }))
    },

    /**
     * Gives previous calendar date range
     * @returns {String|null}
     */
    getPreviousCalendarRange () {
      return (this.previousRanges.calendar || {}).from || null
    },

    /**
     * Tells a today's date
     * @returns {String}
     */
    getToday () {
      return this.getViewedDate.id || moment().startOf('day').toISOString()
    },

    /**
     * Gives the grid display style
     * @returns {String} Grid display style
     */
    getDisplayStyle () {
      return this.$route.params.style || DEFAULT_EDITOR_VIEW
    },

    /**
     * Gives display list items
     * @returns {Array} Array of items to display on the grid
     */
    getDisplayItems () {
      return this.getData || []
    },

    /**
     * Gives curently viewed date
     * @returns {DateTimeString} Currently vuewed date
     */
    getViewedDate () {
      return this.filterFields.date || {}
    },

    /**
     * Tells what date range should be used based on display style
     * @returns {String} What date range to use
     */
    getDataRangeFieldFrmStyle () {
      switch(this.getDisplayStyle) {
        case 'month':
          return 'month'

        case 'agendaWeek':
          return 'week'

        default:
          return 'day'
      }
    },

    /**
     * Gives date range for item filter - Used by campaignViewer provider mixin
     * @returns {Object} An object of {from to} dates
     */
    getDateRange () {
      let {from, to} = (this.filterFields.date || {})
      if (!from) {
        from = moment().toISOString()
      }
      if (!to) {
        to = moment().toISOString()
      }

      return {from, to}
    },

    /**
     * Gives id of currently edited MPE
     * @returns {String}
     */
    getEditedMpeId () {
      return this.$route.params.mpeId
    },

    /**
     * Tells if we are editing a MPE
     * @returns {Boolean}
     */
    isEditingMpe () {
      return !!this.getEditedMpeId
    }
  },

  watch: {
    getDisplayStyle: {
      handler: function (style) {
        if (resolver.isCalendarView(style)) {
          // Tweak date based on calendar type
          let from = moment(this.getPreviousCalendarRange).startOf(this.getDataRangeFieldFrmStyle).toISOString()
          let id = from
          let to = moment(from).endOf(this.getDataRangeFieldFrmStyle).toISOString()

          // If we are switching to week view and start/end month don't match, add a week and 
          if (style === 'agendaWeek') {
            let corrected = makeDates.correctMonthToWeekRange(from, to)
            from = corrected.from
            id = from
            to = corrected.to
          }

          this.$set(this.filterFields, 'date', {
            from,
            id,
            to
          })
        } else {
          this.$set(this.filterFields, 'date', this.previousRanges.grid)
        }
      }
    }
  },

  components: {
    'campaign-grid': CampaignGrid,
    'editor-wrapper': EditorWrapper
  },

  mixins: [
    campaignViewer,
    apiConnector
  ]
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/service/campaignManager/CampaignViewer.vue?5a468c3d